import { useEffect, useState } from "react";

const initialForm = {
  config_name: "Default Configuration",
  consolidation_prompt: "",
  max_words: 1000,
  llm_provider: "gemini",
  llm_model: "",
  llm_api_key: "",
  temperature: 0.2,
  embedding_provider: "huggingface",
  embedding_model: "sentence-transformers/all-MiniLM-L6-v2",
  embedding_dimensions: "",
  embedding_batch_size: 32,
  embedding_api_key: ""
};

function ConfigurationPage() {
  const [form, setForm] = useState({ ...initialForm });
  const [configs, setConfigs] = useState([]);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function loadConfigurations() {
    try {
      const response = await fetch("/api/configuration/list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to load configurations");
      }

      setConfigs(result.data);
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => {
    loadConfigurations();
  }, []);

  function handleChange(e) {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  }

  function resetForm() {
    setForm({ ...initialForm });
    setEditId(null);
  }

  function editConfiguration(config) {
    setEditId(config.id);
    setForm({
      ...initialForm,
      ...config,
      llm_api_key: "",
      embedding_api_key: "",
      embedding_dimensions: config.embedding_dimensions ?? ""
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");

    try {
      const response = await fetch("/api/configuration/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          id: editId,
          max_words: Number(form.max_words),
          temperature: Number(form.temperature),
          embedding_batch_size: Number(form.embedding_batch_size),
          embedding_dimensions:
            form.embedding_dimensions === ""
              ? null
              : Number(form.embedding_dimensions)
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to save configuration");
      }

      setMessage(result.message);
      resetForm();
      await loadConfigurations();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function activateConfiguration(id) {
    setMessage("");
    setError("");

    try {
      const response = await fetch("/api/configuration/activate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to activate configuration");
      }

      setMessage(result.message);
      await loadConfigurations();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="container-fluid py-4">
      <h4>Configuration</h4>
      <p className="text-muted">
        Manage consolidation prompts, word limits, LLM models,
        API keys and embedding settings.
      </p>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSave}>
        <div className="card mb-4">
          <div className="card-header fw-semibold">
            {editId ? "Edit Configuration" : "New Configuration"}
          </div>

          <div className="card-body">
            <div className="mb-3">
              <label className="form-label">Configuration Name</label>
              <input
                className="form-control"
                name="config_name"
                value={form.config_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Consolidation Prompt</label>
              <textarea
                className="form-control"
                name="consolidation_prompt"
                rows="5"
                value={form.consolidation_prompt}
                onChange={handleChange}
                required
              />
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Maximum Output Words</label>
                <input
                  className="form-control"
                  type="number"
                  name="max_words"
                  min="1"
                  value={form.max_words}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Temperature</label>
                <input
                  className="form-control"
                  type="number"
                  name="temperature"
                  min="0"
                  max="2"
                  step="0.1"
                  value={form.temperature}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header fw-semibold">LLM Settings</div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Provider</label>
                <select
                  className="form-select"
                  name="llm_provider"
                  value={form.llm_provider}
                  onChange={handleChange}
                >
                  <option value="gemini">Google Gemini</option>
                  <option value="openai">OpenAI</option>
                  <option value="grok">xAI Grok</option>
                  <option value="ollama">Ollama</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Model Name</label>
                <input
                  className="form-control"
                  name="llm_model"
                  value={form.llm_model}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">API Key</label>
              <input
                className="form-control"
                type="password"
                name="llm_api_key"
                value={form.llm_api_key}
                onChange={handleChange}
                autoComplete="new-password"
                placeholder={
                  editId ? "Leave blank to retain existing key" : "Enter API key"
                }
              />
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header fw-semibold">Embedding Settings</div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Provider</label>
                <select
                  className="form-select"
                  name="embedding_provider"
                  value={form.embedding_provider}
                  onChange={handleChange}
                >
                  <option value="huggingface">HuggingFace</option>
                  <option value="openai">OpenAI</option>
                  <option value="gemini">Google Gemini</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Embedding Model</label>
                <input
                  className="form-control"
                  name="embedding_model"
                  value={form.embedding_model}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Vector Dimensions</label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  name="embedding_dimensions"
                  value={form.embedding_dimensions}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Embedding Batch Size</label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  name="embedding_batch_size"
                  value={form.embedding_batch_size}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Embedding API Key</label>
              <input
                className="form-control"
                type="password"
                name="embedding_api_key"
                value={form.embedding_api_key}
                onChange={handleChange}
                autoComplete="new-password"
                placeholder={
                  editId ? "Leave blank to retain existing key" : "Enter API key"
                }
              />
            </div>
          </div>
        </div>

        <div className="d-flex gap-2 mb-5">
          <button className="btn btn-primary" disabled={saving}>
            {saving
              ? "Saving..."
              : editId
                ? "Update Configuration"
                : "Save Configuration"}
          </button>

          {editId && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={resetForm}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="card">
        <div className="card-header fw-semibold">
          Saved Configurations
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>LLM</th>
                <th>Embedding</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {configs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    No configurations saved.
                  </td>
                </tr>
              ) : (
                configs.map((config) => (
                  <tr key={config.id}>
                    <td>{config.config_name}</td>
                    <td>{config.llm_provider} / {config.llm_model}</td>
                    <td>
                      {config.embedding_provider}
                      <br />
                      <small className="text-muted">
                        {config.embedding_model}
                      </small>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          config.status === 1 ? "bg-success" : "bg-secondary"
                        }`}
                      >
                        {config.status === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => editConfiguration(config)}
                      >
                        Edit
                      </button>

                      {config.status !== 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success"
                          onClick={() => activateConfiguration(config.id)}
                        >
                          Activate
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ConfigurationPage;